import { useState } from 'react';
import { ScaleIcon, TruckIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useStockDisputes, useFleetVans, useResolveDispute } from '../hooks/useStockDisputes';

// Stock disputes.
//
// An engineer logged stock off someone else's van; the owner says it wasn't
// his. Nothing has moved — the stock is still recorded against his van and
// still on his next order — so this queue exists to settle it.
//
// Upholding is the only outcome that changes anything, and what it changes is
// which van the job card names, not the quantities. The stock was genuinely
// used; the argument is only about whose van it came off.

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB',
  { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const qty = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
};

function Dispute({ row, vans }) {
  const resolve = useResolveDispute(row.id);
  const [outcome, setOutcome] = useState('');
  const [reassignTo, setReassignTo] = useState('');
  const [note, setNote] = useState('');

  const lines = Array.isArray(row.lines) ? row.lines : [];
  const done = resolve.isSuccess;

  if (done) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
        <strong>{row.wo_number || 'Job card'}</strong> settled —{' '}
        {resolve.data?.resolution === 'upheld'
          ? `not off ${row.van_registration}${resolve.data?.reassigned_van ? `, moved to ${resolve.data.reassigned_van}` : ', left unassigned'}`
          : `confirmed off ${row.van_registration}`}. Both engineers have been told.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-4 py-3">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="inline-flex items-center gap-1.5 font-semibold text-gray-900">
            <TruckIcon className="h-4 w-4 text-brand" />
            {row.van_registration}
          </span>
          <span className="text-sm text-gray-500">
            {row.owner_name || 'the owner'} says this was not off his van
          </span>
          <span className="ml-auto text-xs text-gray-400">
            disputed {fmtDate(row.resolved_at || row.created_at)}
          </span>
        </div>
        <p className="mt-1 text-sm text-gray-600">
          Logged by <strong>{row.borrower_name || 'another engineer'}</strong> on{' '}
          {row.wo_number || 'a job card'}
          {row.job_address ? ` — ${row.job_address}` : ''}
        </p>
        {row.dispute_reason ? (
          <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm italic text-amber-900">
            “{row.dispute_reason}”
          </p>
        ) : null}
      </div>

      <ul className="divide-y divide-gray-100">
        {lines.map((l, i) => (
          <li key={`${l.sku}-${i}`} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
            <span className="min-w-0">
              <span className="block truncate text-gray-900">{l.name}</span>
              <span className="block text-xs text-gray-400">
                {l.sku}{Number(l.faulty) > 0 ? ' · faulty' : ''}
              </span>
            </span>
            <span className="shrink-0 font-semibold tabular-nums text-gray-900">
              {qty(Number(l.qty) + Number(l.faulty))}
            </span>
          </li>
        ))}
      </ul>

      <div className="border-t border-gray-100 px-4 py-3">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">Ruling</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button" onClick={() => setOutcome('upheld')}
            className={`rounded-md border px-3 py-1.5 text-sm font-medium ${
              outcome === 'upheld'
                ? 'border-amber-400 bg-amber-50 text-amber-800'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            He is right — not off his van
          </button>
          <button
            type="button" onClick={() => { setOutcome('not_upheld'); setReassignTo(''); }}
            className={`rounded-md border px-3 py-1.5 text-sm font-medium ${
              outcome === 'not_upheld'
                ? 'border-emerald-400 bg-emerald-50 text-emerald-800'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            It did come off his van
          </button>
        </div>

        {outcome === 'upheld' ? (
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700" htmlFor={`van-${row.id}`}>
              Which van did it actually come off?
            </label>
            <select
              id={`van-${row.id}`} value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 sm:w-80"
            >
              <option value="">Nobody knows — leave it unassigned</option>
              {(vans || [])
                .filter((v) => v.registration !== row.van_registration)
                .map((v) => (
                  <option key={v.registration} value={v.registration}>
                    {v.registration}{v.driver_name ? ` — ${v.driver_name}` : ''}
                  </option>
                ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              This moves the usage and the replacement onto that van. Quantities are unchanged.
            </p>
          </div>
        ) : null}

        {outcome ? (
          <>
            <label className="mt-3 block text-sm font-medium text-gray-700" htmlFor={`note-${row.id}`}>
              What did you find? <span className="font-normal text-gray-400">(both engineers see this)</span>
            </label>
            <textarea
              id={`note-${row.id}`} rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900"
              placeholder="e.g. Depot CCTV shows the stock came off SA75NVP"
            />
            {resolve.isError ? (
              <p className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {resolve.error?.response?.data?.error || 'Could not save that.'}
              </p>
            ) : null}
            <button
              type="button" disabled={resolve.isPending}
              onClick={() => resolve.mutate({ outcome, reassign_to: reassignTo || null, note })}
              className="mt-3 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
            >
              {resolve.isPending ? 'Saving…' : 'Settle it'}
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

function StockDisputes() {
  const { data, isLoading, isError, error, refetch, isFetching } = useStockDisputes();
  const { data: vans } = useFleetVans();
  const disputes = data?.disputes || [];

  return (
    <div className="p-6">
      <div className="mb-1 flex items-center gap-2">
        <ScaleIcon className="h-6 w-6 text-brand" />
        <h1 className="text-xl font-bold text-gray-900">Stock disputes</h1>
        <button
          type="button" onClick={() => refetch()}
          className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      <p className="mb-5 max-w-3xl text-sm text-gray-500">
        An engineer logged stock off someone else&rsquo;s van and the owner says it was not his.
        Nothing has moved — the stock is still recorded against his van and still on his next
        order — until one of these is settled.
      </p>

      {isLoading ? <p className="text-sm text-gray-400">Loading…</p> : null}
      {isError ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error?.response?.data?.error || 'Could not load the disputes.'}
        </p>
      ) : null}

      {data && !disputes.length ? (
        <p className="rounded-lg border border-dashed border-gray-300 px-4 py-10 text-center text-sm text-gray-400">
          Nothing to settle.
        </p>
      ) : null}

      <div className="space-y-4">
        {disputes.map((d) => <Dispute key={d.id} row={d} vans={vans} />)}
      </div>
    </div>
  );
}

export default StockDisputes;
